import * as RdxPopover from '@radix-ui/react-popover';
import {
  CheckIcon,
  ChevronDownIcon,
  Cross2Icon,
  CrossCircledIcon,
  MagnifyingGlassIcon,
} from '@radix-ui/react-icons';
import { useState, useCallback, useMemo, KeyboardEvent } from 'react';
import cn from '../../app/utils/cn';

const MAX_VISIBLE_CHIPS = 2;

interface IMultiSelectOption {
  value: string;
  label: string;
}

interface IMultiSelectProps {
  options: IMultiSelectOption[];
  value: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  noResultsText?: string;
  error?: string;
  disabled?: boolean;
  className?: string;
}

export default function MultiSelect({
  options,
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  noResultsText,
  error,
  disabled,
  className,
}: IMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const filteredOptions = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return options;
    return options.filter((option) =>
      option.label.toLowerCase().includes(normalized),
    );
  }, [options, query]);

  const handleOpenChange = useCallback((next: boolean) => {
    setOpen(next);
    if (!next) setQuery('');
  }, []);

  const toggleOption = useCallback(
    (optionValue: string) => {
      if (value.includes(optionValue)) {
        onChange(value.filter((v) => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    },
    [value, onChange],
  );

  const removeChip = useCallback(
    (optionValue: string, e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(value.filter((v) => v !== optionValue));
    },
    [value, onChange],
  );

  const handleTriggerKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setOpen((prev) => !prev);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [],
  );

  const handleItemKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>, optionValue: string) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleOption(optionValue);
      }
      if (e.key === 'Escape') {
        setOpen(false);
      }
    },
    [toggleOption],
  );

  const visibleChips = value.slice(0, MAX_VISIBLE_CHIPS);
  const overflowCount = value.length - MAX_VISIBLE_CHIPS;

  return (
    <div className={cn('relative w-full', className)}>
      <RdxPopover.Root open={open} onOpenChange={handleOpenChange}>
        <RdxPopover.Trigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-expanded={open}
            aria-haspopup="listbox"
            onKeyDown={handleTriggerKeyDown}
            className={cn(
              'bg-surface-input border-border-input text-text-primary flex h-10 w-full items-center justify-between rounded border-2 border-solid px-2 text-sm shadow-md outline-none',
              'focus:border-green-600 dark:focus:border-green-400',
              'disabled:cursor-not-allowed disabled:opacity-50',
              !!error && 'border-red-500',
            )}
          >
            <span className="flex flex-1 flex-wrap items-center gap-1 overflow-hidden">
              {value.length === 0 ? (
                <span className="text-text-tertiary">{placeholder}</span>
              ) : (
                <>
                  {visibleChips.map((v) => {
                    const opt = options.find((o) => o.value === v);
                    return (
                      <span
                        key={v}
                        className="flex items-center gap-0.5 rounded bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900 dark:text-green-200"
                      >
                        {opt?.label ?? v}
                        <span
                          role="button"
                          aria-label={`Remover ${opt?.label ?? v}`}
                          tabIndex={-1}
                          onClick={(e) => removeChip(v, e)}
                          className="cursor-pointer rounded hover:text-green-900 dark:hover:text-green-100"
                        >
                          <Cross2Icon className="h-2.5 w-2.5" />
                        </span>
                      </span>
                    );
                  })}
                  {overflowCount > 0 && (
                    <span className="rounded bg-green-50 px-2 py-0.5 text-xs text-green-700 dark:bg-green-900 dark:text-green-200">
                      +{overflowCount}
                    </span>
                  )}
                </>
              )}
            </span>
            <ChevronDownIcon className="text-text-tertiary ml-1 shrink-0" />
          </button>
        </RdxPopover.Trigger>

        <RdxPopover.Content
          className="bg-surface-card border-border-default z-50 mt-1 flex w-[var(--radix-popover-trigger-width)] flex-col gap-1 rounded-lg border p-1 shadow"
          sideOffset={4}
          align="start"
        >
          <div className="relative">
            <MagnifyingGlassIcon className="text-text-tertiary pointer-events-none absolute top-1/2 left-2 size-4 -translate-y-1/2" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') event.preventDefault();
              }}
              placeholder={searchPlaceholder ?? placeholder}
              aria-label={searchPlaceholder ?? placeholder}
              className="bg-surface-input border-border-input text-text-primary placeholder:text-text-tertiary w-full rounded border py-1.5 pr-2 pl-7 text-sm outline-none focus:border-green-600 dark:focus:border-green-400"
            />
          </div>

          {filteredOptions.length === 0 ? (
            <p className="text-text-tertiary px-2 py-3 text-center text-sm">
              {noResultsText ?? ''}
            </p>
          ) : (
            <ul
              role="listbox"
              aria-multiselectable="true"
              className="flex max-h-60 flex-col overflow-y-auto"
            >
              {filteredOptions.map((option) => {
                const checked = value.includes(option.value);
                return (
                  <li key={option.value} role="option" aria-selected={checked}>
                    <div
                      tabIndex={0}
                      onKeyDown={(e) => handleItemKeyDown(e, option.value)}
                      onClick={() => toggleOption(option.value)}
                      className={cn(
                        'flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm',
                        'text-text-primary',
                        'hover:bg-hover-subtle dark:hover:bg-hover-medium',
                        'focus:bg-hover-subtle dark:focus:bg-hover-medium outline-none',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'flex size-4 shrink-0 items-center justify-center rounded border',
                          checked
                            ? 'text-text-on-brand border-green-600 bg-green-600 dark:border-green-500 dark:bg-green-500'
                            : 'border-grey-400 bg-surface-input dark:border-grey-500',
                        )}
                      >
                        {checked && <CheckIcon className="size-3" />}
                      </span>
                      <span>{option.label}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </RdxPopover.Content>
      </RdxPopover.Root>

      {error && (
        <div className="mt-1 flex items-center gap-1 text-xs text-red-500 dark:text-red-400">
          <CrossCircledIcon />
          <small>{error}</small>
        </div>
      )}
    </div>
  );
}
